import { TypographyH5, TypographyP2Bold } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { type TemplateResponse } from '@heidi/templates/contracts';
import { File, Search, Star } from 'lucide-react';

import { SkeletonBlock, SkeletonLine } from './PreviewPanelContent';

const FAVOURITES_VISIBLE_ROWS = 3;
const HIDE_ALL_TEMPLATES_THRESHOLD = 5;

interface DocumentSelectionPreviewProps {
  selectedTemplates: TemplateResponse[];
  createdTemplateName?: string;
}

function FavouriteItem({ name }: { name: string }) {
  return (
    <div className="flex items-center gap-4">
      <Star size={22} className="shrink-0 fill-amber-400 text-amber-400" />
      <TypographyP2Bold className="text-text-primary">{name}</TypographyP2Bold>
    </div>
  );
}

function FavouriteSkeletonItem({ width }: { width: string }) {
  return (
    <div className="flex items-center gap-4">
      <Star size={22} className="shrink-0 text-text-disabled" />
      <SkeletonBlock width={width} variant="muted" />
    </div>
  );
}

function DocTemplateItem({ width }: { width: string }) {
  return (
    <div className="flex items-center gap-4">
      <File size={22} className="shrink-0 text-text-disabled" />
      <SkeletonBlock width={width} variant="muted" />
    </div>
  );
}

const FAVOURITE_SKELETON_WIDTHS = ['w-40', 'w-72', 'w-52'];
const DOC_TEMPLATE_SKELETON_WIDTHS = ['w-80', 'w-48', 'w-64', 'w-56'];

export function DocumentSelectionPreview({
  selectedTemplates,
  createdTemplateName,
}: DocumentSelectionPreviewProps) {
  const { t } = useTranslations();
  const docTemplateSlots = 4;

  const favouriteNames: string[] = [];
  if (createdTemplateName) {
    favouriteNames.push(createdTemplateName);
  }
  selectedTemplates.forEach((tmpl) => favouriteNames.push(tmpl.name));

  const visibleFavourites = favouriteNames.slice(0, FAVOURITES_VISIBLE_ROWS);
  const remainingSkeletons = Math.max(
    0,
    FAVOURITES_VISIBLE_ROWS - visibleFavourites.length
  );
  const hideAllTemplates = favouriteNames.length >= HIDE_ALL_TEMPLATES_THRESHOLD;

  return (
    <div className="flex size-full items-center justify-center overflow-hidden rounded-lg">
      <div className="flex h-[535px] w-[472px] flex-col gap-8 overflow-y-auto rounded-2xl bg-surface p-8 shadow-lg">
        <div className="flex items-center gap-3 rounded-lg border border-border px-4 py-3">
          <Search size={20} className="text-text-disabled" />
          <SkeletonLine width="w-[172px]" variant="muted" />
        </div>

        <div className="flex flex-col">
          <TypographyH5 className="mb-6 text-text-secondary">
            {t('signup.favourites')}
          </TypographyH5>
          <div className="flex flex-col gap-4">
            {visibleFavourites.map((name, i) => (
              <FavouriteItem key={`fav-${i}`} name={name} />
            ))}
            {Array.from({ length: remainingSkeletons }).map((_, i) => (
              <FavouriteSkeletonItem
                key={`fav-skeleton-${i}`}
                width={
                  FAVOURITE_SKELETON_WIDTHS[i % FAVOURITE_SKELETON_WIDTHS.length] ??
                  'w-40'
                }
              />
            ))}
          </div>
        </div>

        {!hideAllTemplates && (
          <div className="flex flex-col">
            <TypographyH5 className="mb-6 text-text-secondary">
              {t('signup.allTemplates')}
            </TypographyH5>
            <div className="flex flex-col gap-6">
              {Array.from({ length: docTemplateSlots }).map((_, i) => (
                <DocTemplateItem
                  key={`doc-skeleton-${i}`}
                  width={
                    DOC_TEMPLATE_SKELETON_WIDTHS[
                      i % DOC_TEMPLATE_SKELETON_WIDTHS.length
                    ] ?? 'w-64'
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
